import {
  AlertCircleIcon,
  AlertTriangleIcon,
  Button,
  ButtonStyle,
  CheckIcon,
  EllipsisIcon,
  Spinner,
} from "@prequel/shared-components";

import { TestConnectionResult } from "@/api/client";
import { requestIsProcessing } from "@/types/common";

type TestConnectionCardProps = {
  test: TestConnectionResult;
  request: TestConnectionResult;
  connectionType: "source" | "destination";
  usesPreparedConnection: boolean;
  onTestConnection: () => void;
  className?: string;
};
const TestConnectionCard = ({
  test,
  request,
  connectionType,
  usesPreparedConnection,
  onTestConnection,
  className = "",
}: TestConnectionCardProps) => {
  const testIsSuccess = test.status === "success";
  const testIsError = test.status === "error";
  const connectionTestIsProcessing = requestIsProcessing(test);
  const connectionRequestIsProcessing = requestIsProcessing(request);
  return (
    <div
      className={`
        relative pt-4
        ${className}
      `}
    >
      <div
        className="
          overflow-hidden rounded-lg border border-gray-300 shadow-sm
          focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500
        "
      >
        <div className="p-4">
          <div className="flex">
            <div className="shrink-0">
              <div
                className={`
                  mx-auto flex size-10 items-center justify-center rounded-full
                  ${
                    testIsSuccess
                      ? "bg-emerald-100"
                      : testIsError
                        ? "bg-red-100"
                        : connectionTestIsProcessing
                          ? "bg-gray-100"
                          : "bg-gray-100"
                  }
                `}
              >
                {testIsSuccess && <CheckIcon className="size-5 text-emerald-600" aria-hidden="true" />}
                {testIsError && <AlertCircleIcon className="size-5 text-red-600" aria-hidden="true" />}
                {connectionTestIsProcessing && <EllipsisIcon className="size-5 text-gray-600" aria-hidden="true" />}
                {!test.status && <AlertTriangleIcon className="size-5 text-gray-600" aria-hidden="true" />}
              </div>
            </div>
            <div className="mt-1 ml-3">
              <h3 className="text-lg font-medium text-gray-800">
                {testIsSuccess && "Connection successful!"}
                {testIsError && `Error connecting to ${connectionType}.`}
                {connectionTestIsProcessing && "Testing connection..."}
                {!test.status && `Test connection${usesPreparedConnection ? " before saving." : " status."} `}
              </h3>
              <div className="my-2 text-sm text-gray-700">
                <p>
                  {testIsSuccess &&
                    `Connection was successfully established to this ${connectionType}.${
                      usesPreparedConnection ? ` The ${connectionType} details can now be saved.` : ""
                    }`}
                  {testIsError && (
                    <>
                      <span className="mb-4 block">Try updating the {connectionType} details and try again.</span>
                      <code className="text-xs" style={{ wordBreak: "break-word" }}>
                        {test.message}
                      </code>
                    </>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
        <div aria-hidden="true">
          <div className="h-px" />
          <div className="py-2">
            <div className="py-px">
              <div className="h-9" />
            </div>
          </div>
        </div>
        <div className="absolute inset-x-px bottom-0">
          <div
            className="
              flex items-center justify-between space-x-3 border-t border-gray-200 p-2
              sm:px-3
            "
          >
            <div className="flex">
              <div className="shrink-0">
                <Button
                  onClick={onTestConnection}
                  type={ButtonStyle.PRIMARY}
                  disabled={connectionTestIsProcessing || connectionRequestIsProcessing}
                  text={
                    connectionTestIsProcessing ? (
                      <div className="flex">
                        <Spinner.Inline className="mr-3 text-white" />
                        Testing Connection...
                      </div>
                    ) : (
                      "Test Connection"
                    )
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestConnectionCard;
